import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { mobile, tablet } from "../../../design-system/tokens/breakpoints";
import { focusRing } from "../../../design-system/focusRing";
import { useDispatch, useSelector } from "react-redux";
import {
    PathwayCenteredTableCellContent,
    PathwayTable,
    PathwayTableCellContent,
    PathwayTableContentRow,
    PathwayTableHeader,
    PathwayTableHeaderRow,
    TableContentDividerLine,
} from "../../../_UI/StyledComponents/PathwayTable";
import { PathwayButton } from "../../../_UI/Components/PathwayButtons";
import stringUtils from "../../../_services/string-utils";
import PathwayConnectionChip from "../../../_UI/Components/PathwayConnectionChip";
import PathwayStatusPill from "../../../_UI/Components/PathwayStatusPill";
import { getIntegrationIcon, getIntegrationLogo } from "../../../_statics/IntegrationBrand";
import {
    connectionStatusSeverity,
    isAwaitingSetup,
    isExpiredShopifyConnection,
    isInvalidShopifyConnection,
    isLegacyShopifyConnection,
    isStalledPaypalSetup,
} from "./connectionStatus";
import PathwayRowActions from "../../../_UI/Components/PathwayRowActions";
import PathwayHint from "../../../_UI/Components/PathwayHint";
import { PathwayIconButton } from "../../../_UI/StyledComponents/PathwayIcons";
import { WarningMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import { ShopifyMigrationOutcome, getDatevConnections } from "../services/clients.actions";
import LoadingSpinner from "../../../_UI/Components/LoadingSpinner";
import { AuthDataStatus } from "../../../_statics/AuthDataStatus";
import { UserRole } from "../../../_statics/UserStatics";

// Three button states: idle, armed (the second click confirms), and in flight. The exchange also verifies
// webhooks and charges, which takes seconds, so the wait has to be visible on the button itself.
const migrateButtonContent = (migrating, armed) => {
    if (migrating) {
        return (
            <MigratingLabel data-cy="shopify-connection-migrating">
                <LoadingSpinner size="1.2rem" loading={true} primary={false} />
                Migriere…
            </MigratingLabel>
        );
    }
    return armed ? "Wirklich migrieren?" : "Migrieren";
};

/**
 * The exchange answers HTTP 200 even when it was rejected, skipped or failed, so each outcome needs its own
 * sentence: a rejection is final and only the merchant can repair it, a failure is transient and leaves the row
 * legacy, a skip means a concurrent writer held the credential and it is worth another try, and an untouched row
 * must not be reported as any of them.
 */
const MIGRATION_FAILURE_TEXT = {
    [ShopifyMigrationOutcome.REJECTED]: "Shopify hat die Migration abgelehnt. Der Händler muss die App erneut öffnen.",
    [ShopifyMigrationOutcome.FAILED]: "Migration fehlgeschlagen. Bitte später erneut versuchen.",
    [ShopifyMigrationOutcome.SKIPPED]:
        "Die Migration wurde übersprungen, weil die Verbindung gerade aktualisiert wird. Bitte erneut versuchen.",
    [ShopifyMigrationOutcome.NOT_RUN]: "Die Migration wurde nicht ausgeführt.",
    [ShopifyMigrationOutcome.REFUSED]: "Migration fehlgeschlagen. Bitte den Status der Verbindung prüfen.",
};

// What the pill says, so it cannot contradict the sentence underneath it.
const MIGRATION_PILL_LABEL = {
    [ShopifyMigrationOutcome.MIGRATED]: "Migriert",
    [ShopifyMigrationOutcome.REJECTED]: "Abgelehnt",
    [ShopifyMigrationOutcome.FAILED]: "Migration fehlgeschlagen",
    [ShopifyMigrationOutcome.SKIPPED]: "Übersprungen",
    [ShopifyMigrationOutcome.NOT_RUN]: "Nicht ausgeführt",
    [ShopifyMigrationOutcome.REFUSED]: "Nicht möglich",
};

const MIGRATION_VERIFICATION_WARNING = "Webhooks bzw. Abrechnung konnten nicht vollständig geprüft werden.";

// A pre-flight refusal carries the backend's own message, which tells the three refusals apart; the counter-based
// outcomes have no message and are named here. The fallback names an outcome this build does not know rather
// than claiming the exchange never ran.
const migrationFailureText = (outcome) =>
    outcome.message ||
    MIGRATION_FAILURE_TEXT[outcome.status] ||
    MIGRATION_FAILURE_TEXT[ShopifyMigrationOutcome.REFUSED];

const migrationPillLabel = (outcome) =>
    MIGRATION_PILL_LABEL[outcome.status] || MIGRATION_PILL_LABEL[ShopifyMigrationOutcome.FAILED];

const ClientsScreenClientsTabContent = (props) => {
    const dispatch = useDispatch();
    const clientReducer = useSelector((state) => state.client);
    const clients = useSelector((state) => state.client.clients);

    useEffect(() => {
        dispatch(getDatevConnections());
    }, [dispatch]);

    const user = useSelector((state) => state.authentication.user);

    const isSuperUser = useMemo(
        () => user && (user.userRole === UserRole.SUPER_USER || user.userRole === UserRole.TECHNICAL_USER),
        [user]
    );

    // Two clicks rather than a window.confirm: the exchange cannot be undone, and a native dialog cannot be
    // driven from a browser test. Only one row can be armed at a time; the armed state falls back after a few
    // seconds so a forgotten first click cannot be completed by a later, unrelated one, and a confirm arriving
    // within the dwell window is ignored so a double click cannot skip the question.
    const [armedMigrationUUID, setArmedMigrationUUID] = useState(null);
    // Both maps are keyed by the credential's externalUUID, because the exchange is per row and takes seconds:
    // one shared slot let a second row release the first row's in-flight button (and drop its result) while its
    // irreversible POST was still running.
    const [migrationOutcomes, setMigrationOutcomes] = useState({});
    const [migratingUUIDs, setMigratingUUIDs] = useState({});
    const armedTimeoutRef = useRef(null);
    const armedAtRef = useRef(0);
    const outcomeTimeoutsRef = useRef({});
    const mountedRef = useRef(true);

    useEffect(
        () => () => {
            mountedRef.current = false;
            clearTimeout(armedTimeoutRef.current);
            Object.values(outcomeTimeoutsRef.current).forEach(clearTimeout);
        },
        []
    );

    const forgetMigrationOutcome = useCallback((uuid) => {
        setMigrationOutcomes((outcomes) => {
            if (!(uuid in outcomes)) {
                return outcomes;
            }
            const remaining = { ...outcomes };
            delete remaining[uuid];
            return remaining;
        });
    }, []);

    const onMigrateClick = useCallback(
        async (connection) => {
            const uuid = connection.externalUUID;
            if (migratingUUIDs[uuid]) {
                return;
            }
            if (armedMigrationUUID !== uuid) {
                clearTimeout(armedTimeoutRef.current);
                armedAtRef.current = Date.now();
                clearTimeout(outcomeTimeoutsRef.current[uuid]);
                forgetMigrationOutcome(uuid);
                setArmedMigrationUUID(uuid);
                armedTimeoutRef.current = setTimeout(() => setArmedMigrationUUID(null), MIGRATE_ARMED_TIMEOUT_MS);
                return;
            }
            if (Date.now() - armedAtRef.current < MIGRATE_CONFIRM_DWELL_MS) {
                // A double click would otherwise confirm the exchange in the frame after it was armed, before
                // "Wirklich migrieren?" could be read. The row stays armed and its 5 s expiry keeps running.
                return;
            }
            clearTimeout(armedTimeoutRef.current);
            setArmedMigrationUUID(null);
            setMigratingUUIDs((running) => ({ ...running, [uuid]: true }));
            const outcome = await props.migrateShopifyConnection(connection);
            // A successful exchange returns the row on the new type, and the quiet refresh then drops this
            // button, so the answer can arrive after the component is gone.
            if (!mountedRef.current) {
                return;
            }
            setMigratingUUIDs((running) => {
                const stillRunning = { ...running };
                delete stillRunning[uuid];
                return stillRunning;
            });
            setMigrationOutcomes((outcomes) => ({ ...outcomes, [uuid]: outcome }));
            clearTimeout(outcomeTimeoutsRef.current[uuid]);
            if (outcome?.success && !outcome.verificationIncomplete) {
                // A failure and an incomplete verification both stay until the next attempt on that row, because
                // both need acting on; a clean success only needs to say that the exchange happened, and the
                // refreshed row comes back on the new type and drops the button by itself.
                outcomeTimeoutsRef.current[uuid] = setTimeout(
                    () => forgetMigrationOutcome(uuid),
                    MIGRATE_NOTICE_TIMEOUT_MS
                );
            }
        },
        [armedMigrationUUID, migratingUUIDs, forgetMigrationOutcome, props]
    );

    const stripeConnectionOf = (client) =>
        (client.connectedTo || []).find((connection) => connection.type === "STRIPE_CONNECT");

    const getStripeDashboardUrl = (isSuperUser, customerStripeEndpoint) => {
        const pathwayStripeEndpoint = process.env.REACT_APP_PATHWAY_STRIPE_CONNECTION_ENDPOINT;

        if (isSuperUser && pathwayStripeEndpoint) {
            return `https://dashboard.stripe.com/${pathwayStripeEndpoint}/connect/view-as/${customerStripeEndpoint}/balance/overview`;
        }

        return `https://dashboard.stripe.com/${customerStripeEndpoint}`;
    };

    return (
        <>
            <PathwayTable>
                <TopTableContent>
                    <WarningMessage marginless messages={clientReducer.datevConnectionsWarningMessage} />
                </TopTableContent>
                <TableHeader>
                    <PathwayTableHeader>Mandant</PathwayTableHeader>
                    <PathwayTableHeader>Verbindungen</PathwayTableHeader>
                    <PathwayTableHeader>DATEV</PathwayTableHeader>
                    <PathwayTableHeader />
                </TableHeader>

                {clients.map((client, index1) => (
                    <React.Fragment key={client.externalUUID}>
                        <TableRow>
                            <ClientCell>
                                <ClientName>{client.name}</ClientName>
                                {client.note && <ClientNote>{client.note}</ClientNote>}
                            </ClientCell>
                            <ButtonCell>
                                <PathwayButton
                                    color="tinted"
                                    dataCy={"add-connection-button-" + index1}
                                    onClick={() => props.createNewConnectedAuthDataForClient(client)}
                                >
                                    <i className="fa fa-plus" aria-hidden="true" />
                                    Verbindung hinzufügen
                                </PathwayButton>
                                {client.connectedTo.map((connection, index2) => {
                                    const migrating = !!migratingUUIDs[connection.externalUUID];
                                    const armed = armedMigrationUUID === connection.externalUUID;
                                    const outcome = migrationOutcomes[connection.externalUUID];
                                    return (
                                        <ConnectionRow key={connection.externalUUID}>
                                            <PathwayConnectionChip
                                                title={connection.title}
                                                logo={getIntegrationLogo(connection.type)}
                                                icon={getIntegrationIcon(connection.type)}
                                                status={connectionStatusSeverity(connection.status)}
                                                dataCy={
                                                    stringUtils.formatUppercaseIdentifierToLowercase(connection.type) +
                                                    "-connection-button-" +
                                                    index1 +
                                                    "-" +
                                                    index2
                                                }
                                                onClick={() => props.editExistingConnectedAuthData(connection)}
                                            >
                                                {isAwaitingSetup(connection) && (
                                                    <LoadingSpinner size="1.6rem" loading={true} primary={false} />
                                                )}
                                                {isStalledPaypalSetup(connection) && (
                                                    <PathwayStatusPill severity="warning">
                                                        Bitte erneut versuchen
                                                    </PathwayStatusPill>
                                                )}
                                                {connection.status === AuthDataStatus.REVOKED && (
                                                    <PathwayStatusPill severity="error">
                                                        Deinstalliert
                                                    </PathwayStatusPill>
                                                )}
                                                {isExpiredShopifyConnection(connection) && (
                                                    <PathwayStatusPill
                                                        severity="warning"
                                                        dataCy="shopify-connection-expired"
                                                    >
                                                        Abgelaufen – App in Shopify erneut öffnen
                                                    </PathwayStatusPill>
                                                )}
                                                {isInvalidShopifyConnection(connection) && (
                                                    <PathwayStatusPill
                                                        severity="warning"
                                                        dataCy="shopify-connection-invalid"
                                                    >
                                                        Ungültig – App in Shopify erneut öffnen
                                                    </PathwayStatusPill>
                                                )}
                                            </PathwayConnectionChip>
                                            {isSuperUser && isLegacyShopifyConnection(connection) && (
                                                <MigrateButton
                                                    type="button"
                                                    disabled={migrating}
                                                    data-cy={"shopify-connection-migrate-" + connection.externalUUID}
                                                    onClick={() => onMigrateClick(connection)}
                                                >
                                                    {migrateButtonContent(migrating, armed)}
                                                </MigrateButton>
                                            )}
                                            {outcome && (
                                                <MigrationOutcome>
                                                    <PathwayStatusPill
                                                        severity={outcome.success ? "ok" : "error"}
                                                        dataCy={
                                                            outcome.success
                                                                ? "shopify-connection-migrated"
                                                                : "shopify-connection-migrate-failed"
                                                        }
                                                    >
                                                        {migrationPillLabel(outcome)}
                                                    </PathwayStatusPill>
                                                    {!outcome.success && (
                                                        <MigrationOutcomeText
                                                            title={migrationFailureText(outcome)}
                                                            data-cy="shopify-connection-migrate-message"
                                                        >
                                                            {migrationFailureText(outcome)}
                                                        </MigrationOutcomeText>
                                                    )}
                                                    {outcome.success && outcome.verificationIncomplete && (
                                                        <MigrationOutcomeWarning data-cy="shopify-connection-migrate-warning">
                                                            {MIGRATION_VERIFICATION_WARNING}
                                                        </MigrationOutcomeWarning>
                                                    )}
                                                </MigrationOutcome>
                                            )}
                                        </ConnectionRow>
                                    );
                                })}
                            </ButtonCell>
                            <DatevCell>
                                <DatevPair data-cy="datev-row">
                                    <DatevLabel>Berater</DatevLabel>
                                    <DatevValue>{client.consultantNumber || "—"}</DatevValue>
                                    <DatevSeparator aria-hidden="true">·</DatevSeparator>
                                    <DatevLabel>Mandat</DatevLabel>
                                    <DatevValue>{client.clientNumber || "—"}</DatevValue>
                                </DatevPair>
                                {(client.skr || client.accountNumberLength) && (
                                    <DatevPair>
                                        {client.skr && (
                                            <>
                                                <DatevLabel>SKR</DatevLabel>
                                                <DatevValue>{client.skr?.replace("_", " ")}</DatevValue>
                                            </>
                                        )}
                                        {client.skr && client.accountNumberLength && (
                                            <DatevSeparator aria-hidden="true">·</DatevSeparator>
                                        )}
                                        {client.accountNumberLength && (
                                            <>
                                                <DatevLabel>SKL</DatevLabel>
                                                <DatevValue>{client.accountNumberLength}-stellig</DatevValue>
                                            </>
                                        )}
                                    </DatevPair>
                                )}
                            </DatevCell>
                            <ActionsCell>
                                <PathwayRowActions>
                                    {(() => {
                                        const stripeConn = stripeConnectionOf(client);
                                        return (
                                            stripeConn && (
                                                <PathwayHint hint="Zum Stripe Connect Account">
                                                    <PathwayIconButton
                                                        type="button"
                                                        className="pw-icon-brand"
                                                        aria-label="Zum Stripe Connect Account"
                                                        data-cy={`stripe-connect-button-${index1}`}
                                                        onClick={() =>
                                                            window.open(
                                                                getStripeDashboardUrl(isSuperUser, stripeConn.endpoint),
                                                                "_blank",
                                                                "noopener,noreferrer"
                                                            )
                                                        }
                                                    >
                                                        <i
                                                            className="fa fa-arrow-up-right-from-square"
                                                            aria-hidden="true"
                                                        />
                                                    </PathwayIconButton>
                                                </PathwayHint>
                                            )
                                        );
                                    })()}
                                    <PathwayHint hint="Mandant bearbeiten">
                                        <PathwayIconButton
                                            type="button"
                                            className="pw-icon-brand"
                                            aria-label="Mandant bearbeiten"
                                            data-cy={"client-edit-button-" + index1}
                                            onClick={() => props.openEditClientModal(client)}
                                        >
                                            <i className="fa fa-pen" aria-hidden="true" />
                                        </PathwayIconButton>
                                    </PathwayHint>
                                    <PathwayHint hint="Mandant löschen">
                                        <PathwayIconButton
                                            type="button"
                                            className="pw-icon-danger"
                                            aria-label="Mandant löschen"
                                            data-cy={"client-delete-button-" + index1}
                                            onClick={() => props.openDeleteClientModal(client)}
                                        >
                                            <i className="fa fa-trash" aria-hidden="true" />
                                        </PathwayIconButton>
                                    </PathwayHint>
                                </PathwayRowActions>
                            </ActionsCell>
                        </TableRow>
                        <TableContentDividerLine />
                    </React.Fragment>
                ))}
            </PathwayTable>
        </>
    );
};

export default ClientsScreenClientsTabContent;

// How long the armed "Wirklich migrieren?" state waits for its confirm, and how long a clean success pill stays.
// Independent knobs that happen to share a value.
const MIGRATE_ARMED_TIMEOUT_MS = 5000;
const MIGRATE_NOTICE_TIMEOUT_MS = 5000;
// The confirm click has to arrive after the armed label has been on screen long enough to read.
const MIGRATE_CONFIRM_DWELL_MS = 400;

// The chip itself is a <button>, so the migrate control cannot live inside it — a button inside a button is
// invalid markup and React warns about the nesting. The connection therefore gets a row of its own: chip, the
// control beside it, and the outcome underneath both.
const ConnectionRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: var(--gap-s);
    row-gap: 4px;
    min-width: 0;
`;

const MigrationOutcome = styled.div`
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--gap-s);
    min-width: 0;
`;

// The exchange landed, so the row keeps its success pill; the webhook and charge verification that the runbook
// asks for did not, and that has to be said on a line of its own rather than replace the outcome.
const MigrationOutcomeWarning = styled.span`
    flex: 1 0 100%;
    min-width: 0;
    font-size: var(--pw-text-sm);
    color: var(--text-muted);
`;

// The failure text is the whole point of showing anything here — for a pre-flight refusal it is the backend's own
// wording — so it is rendered in full and also carried in the title for rows too narrow for it.
const MigrationOutcomeText = styled.span`
    min-width: 0;
    font-size: var(--pw-text-sm);
    color: var(--text-body);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// The in-flight state of the button, hooked separately so the button's own data-cy stays on the same element
// across the flip.
const MigratingLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: var(--gap-xs);
`;

// Sized and coloured like the status pills in the chip next to it, so the row keeps one line of small elements
// rather than gaining a second control that competes with the chip itself.
const MigrateButton = styled.button`
    ${focusRing}
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: var(--gap-xs);
    padding: 4px 10px;
    border: 1px solid var(--pw-warning-border);
    border-radius: var(--pw-radius-pill);
    background: var(--surface-card);
    color: var(--pw-warning-fg);
    font: inherit;
    font-size: var(--pw-text-sm);
    font-weight: var(--pw-weight-semibold);
    line-height: var(--pw-leading-snug);
    white-space: nowrap;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: var(--pw-warning-fg);
    }

    &:disabled {
        cursor: default;
    }
`;

const DESKTOP_COLS = "28fr 26fr 26fr 20fr";
const DESKTOP_AREAS = '"client connections datev actions"';

const TableHeader = styled(PathwayTableHeaderRow)`
    grid-template-columns: ${DESKTOP_COLS};

    ${tablet} {
        grid-template-columns: 1fr auto;
        & > :nth-child(3),
        & > :nth-child(4) {
            display: none;
        }
    }

    ${mobile} {
        display: none;
    }
`;

const TableRow = styled(PathwayTableContentRow)`
    grid-template-columns: ${DESKTOP_COLS};
    grid-template-areas: ${DESKTOP_AREAS};

    ${tablet} {
        grid-template-columns: 180px 1fr auto;
        grid-template-rows: auto auto;
        grid-template-areas:
            "client connections actions"
            "datev  connections .      ";
        row-gap: 0;
        column-gap: var(--gap-l);
    }

    ${mobile} {
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto auto;
        grid-template-areas:
            "client  actions    "
            "connections connections"
            "datev   datev      ";
        row-gap: var(--gap-s);
        column-gap: var(--gap-m);
    }
`;

const ClientName = styled.div`
    font-size: var(--pw-text-md);
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ClientNote = styled.div`
    margin-top: 3px;
    font-size: var(--pw-text-sm);
    font-weight: var(--pw-weight-light);
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DatevPair = styled.div`
    font-size: var(--pw-text-hint);
    color: var(--text-body);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DatevLabel = styled.span`
    font-weight: var(--pw-weight-light);
    color: var(--text-muted);
`;

const DatevValue = styled.span`
    margin-left: 4px;
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
`;

const DatevSeparator = styled.span`
    margin: 0 6px;
    color: var(--pw-neutral-450);
`;

const ClientCell = styled(PathwayTableCellContent)`
    grid-area: client;
`;

const ButtonCell = styled(PathwayTableCellContent)`
    grid-area: connections;
    row-gap: var(--gap-s);
    align-self: start;
    align-content: start;
`;

const DatevCell = styled(PathwayTableCellContent)`
    grid-area: datev;
    align-self: center;

    ${tablet} {
        align-self: end;
        padding-bottom: 4px;
    }

    ${mobile} {
        align-self: start;
    }
`;

const ActionsCell = styled(PathwayTableCellContent)`
    grid-area: actions;
    align-self: start;
`;

const TopTableContent = styled.div`
    display: grid;
    grid-template-columns: auto max-content;
    column-gap: var(--gap-l);
    align-items: end;
`;
